import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { CustomEvent } from './custom-event';
import { GtmCustomEventService } from './gtm-custom-event.service';

const PUBLIC_GTM_ID = '__PUBLIC_GTM_ID__';
const PARENT_MESSAGE_SOURCE = 'circlek-store-locator';
const PARENT_MESSAGE_TYPE = 'gtm-event';
const GTM_SCRIPT = `
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', '${PUBLIC_GTM_ID}');
`;

declare global {
  interface Window {
    dataLayer?: any;
  }
}

@Component({
  selector: 'app-gtm-script',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GtmScriptComponent implements OnInit, AfterViewInit {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  readonly publicGtmId = input.required<string>();

  ngAfterViewInit(): void {
    this.injectGtmScript();
    this.injectGtmNoscript();
  }

  ngOnInit(): void {
    this.gtmCustomEventService.customEvent$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => this.doesEventExists(event)),
      )
      .subscribe((event: CustomEvent | undefined) => {
        this.createDataLayerIfNotExists();
        this.addCustomEventToDataLayer(event);
        this.forwardEventToParent(event);
      });
  }

  private injectGtmScript(): void {
    const script = this.renderer.createElement('script') as HTMLScriptElement;

    script.id = 'google-analytics';
    script.textContent = GTM_SCRIPT.replace(PUBLIC_GTM_ID, this.publicGtmId());
    this.renderer.appendChild(this.document.head, script);
    this.destroyRef.onDestroy(() => this.renderer.removeChild(this.document.head, script));
  }

  private injectGtmNoscript(): void {
    const noscript = this.renderer.createElement('noscript') as HTMLElement;
    const iframe = this.renderer.createElement('iframe') as HTMLIFrameElement;

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.publicGtmId()}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    this.renderer.appendChild(noscript, iframe);
    this.renderer.appendChild(this.document.body, noscript);
    this.destroyRef.onDestroy(() => this.renderer.removeChild(this.document.body, noscript));
  }

  private addCustomEventToDataLayer(event: CustomEvent | undefined): void {
    if (this.doesEventExists(event)) {
      window.dataLayer.push(event);
    }
  }

  private forwardEventToParent(event: CustomEvent | undefined): void {
    const win = this.document.defaultView;

    if (!win || win.parent === win) {
      return;
    }

    win.parent.postMessage({ source: PARENT_MESSAGE_SOURCE, type: PARENT_MESSAGE_TYPE, payload: event }, '*');
  }

  private doesEventExists(event: CustomEvent | undefined): boolean {
    return !!event;
  }

  private createDataLayerIfNotExists(): void {
    if (!window?.dataLayer) {
      window.dataLayer = [];
    }
  }
}
